import {
  FC,
  useState,
  useEffect,
  useMemo,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { getKey, setKey } from "../../Global/history";
import { warehouse as warehouseStrings } from "../../utils/storeString";
import APICallService from "../../api/apiCallService";
import { multipleWarehouse } from "../../api/apiEndPoints";

export const ALL_WAREHOUSES_VALUE = "all";

export interface WarehouseItem {
  value: string;
  label: string;
  title: string;
  _id: string;
  defaultWarehouse?: boolean;
  district?: Array<{
    name: string;
    reference: string;
  }>;
}

export const ALL_WAREHOUSES_OPTION: WarehouseItem = {
  value: ALL_WAREHOUSES_VALUE,
  label: "All Warehouses",
  title: "All Warehouses",
  _id: ALL_WAREHOUSES_VALUE,
};

type WarehouseContextProps = {
  selectedWarehouse: WarehouseItem | null;
  setSelectedWarehouse: (warehouse: WarehouseItem | null) => void;
  selectedWarehouses: WarehouseItem[];
  setSelectedWarehouses: (warehouses: WarehouseItem[]) => void;
  selectedWarehouseIds: string[];
  isAllWarehousesSelected: boolean;
  warehouseList: WarehouseItem[];
  warehouseOptions: WarehouseItem[];
  loading: boolean;
  fetchWarehouseList: () => Promise<void>;
};

const initWarehouseContextState: WarehouseContextProps = {
  selectedWarehouse: null,
  setSelectedWarehouse: () => {},
  selectedWarehouses: [ALL_WAREHOUSES_OPTION],
  setSelectedWarehouses: () => {},
  selectedWarehouseIds: [],
  isAllWarehousesSelected: true,
  warehouseList: [],
  warehouseOptions: [ALL_WAREHOUSES_OPTION],
  loading: false,
  fetchWarehouseList: async () => {},
};

const WarehouseContext = createContext<WarehouseContextProps>(
  initWarehouseContextState
);

export const useWarehouse = () => {
  return useContext(WarehouseContext);
};

interface WarehouseProviderProps {
  children: ReactNode;
}

const isAllOption = (warehouse?: WarehouseItem | null) =>
  warehouse?.value === ALL_WAREHOUSES_VALUE;

const getRealWarehouses = (warehouses: WarehouseItem[] = []) =>
  warehouses.filter((warehouse) => !isAllOption(warehouse));

const normalizeSelectedWarehouses = (
  warehouses: WarehouseItem[] | null | undefined,
  warehouseList: WarehouseItem[]
): WarehouseItem[] => {
  if (!warehouses?.length) {
    return [ALL_WAREHOUSES_OPTION];
  }

  const hasAll = warehouses.some(isAllOption);
  const realSelected = getRealWarehouses(warehouses).filter((warehouse) =>
    warehouseList.some((item) => item.value === warehouse.value)
  );

  if (hasAll || realSelected.length === 0) {
    return [ALL_WAREHOUSES_OPTION];
  }

  if (
    warehouseList.length > 0 &&
    realSelected.length === warehouseList.length
  ) {
    return [ALL_WAREHOUSES_OPTION];
  }

  return realSelected;
};

const resolveStoredWarehouses = (
  warehouseList: WarehouseItem[]
): WarehouseItem[] => {
  const storedWarehouses = getKey(warehouseStrings.warehouseRef) as any;

  if (Array.isArray(storedWarehouses)) {
    return normalizeSelectedWarehouses(storedWarehouses, warehouseList);
  }

  if (storedWarehouses && typeof storedWarehouses === "object") {
    if (isAllOption(storedWarehouses)) {
      return [ALL_WAREHOUSES_OPTION];
    }

    const matched = warehouseList.find(
      (item) => item._id === storedWarehouses._id || item.value === storedWarehouses.value
    );
    return matched ? [matched] : [ALL_WAREHOUSES_OPTION];
  }

  return [ALL_WAREHOUSES_OPTION];
};

export const WarehouseProvider: FC<WarehouseProviderProps> = ({ children }) => {
  const [selectedWarehouses, setSelectedWarehousesState] = useState<
    WarehouseItem[]
  >([ALL_WAREHOUSES_OPTION]);
  const [warehouseList, setWarehouseList] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const isAllWarehousesSelected = useMemo(
    () =>
      selectedWarehouses.length === 0 ||
      selectedWarehouses.some(isAllOption),
    [selectedWarehouses]
  );

  const selectedWarehouseIds = useMemo(() => {
    if (isAllWarehousesSelected) {
      return warehouseList.map((warehouse) => warehouse.value);
    }
    return getRealWarehouses(selectedWarehouses).map(
      (warehouse) => warehouse.value
    );
  }, [isAllWarehousesSelected, selectedWarehouses, warehouseList]);

  const selectedWarehouse = useMemo(() => {
    if (isAllWarehousesSelected) return null;
    const realSelected = getRealWarehouses(selectedWarehouses);
    return realSelected.length === 1 ? realSelected[0] : null;
  }, [isAllWarehousesSelected, selectedWarehouses]);

  const warehouseOptions = useMemo(
    () => [ALL_WAREHOUSES_OPTION, ...warehouseList],
    [warehouseList]
  );

  const persistSelectedWarehouses = (warehouses: WarehouseItem[]) => {
    const normalized = normalizeSelectedWarehouses(warehouses, warehouseList);
    setSelectedWarehousesState(normalized);
    setKey(warehouseStrings.warehouseRef, normalized);
  };

  const setSelectedWarehouses = (warehouses: WarehouseItem[]) => {
    if (!warehouses?.length) {
      persistSelectedWarehouses([ALL_WAREHOUSES_OPTION]);
      return;
    }

    const previousHadAll = selectedWarehouses.some(isAllOption);
    const nextHasAll = warehouses.some(isAllOption);
    const realSelected = getRealWarehouses(warehouses);

    // Selecting "All Warehouses" clears individual selections.
    if (nextHasAll && !previousHadAll) {
      persistSelectedWarehouses([ALL_WAREHOUSES_OPTION]);
      return;
    }

    // Selecting a specific warehouse while "All" is active replaces All.
    if (previousHadAll && realSelected.length > 0) {
      persistSelectedWarehouses(realSelected);
      return;
    }

    persistSelectedWarehouses(realSelected);
  };

  const setSelectedWarehouse = (warehouse: WarehouseItem | null) => {
    if (!warehouse || isAllOption(warehouse)) {
      persistSelectedWarehouses([ALL_WAREHOUSES_OPTION]);
      return;
    }
    persistSelectedWarehouses([warehouse]);
  };

  const fetchWarehouseList = async () => {
    setLoading(true);
    try {
      const apiService = new APICallService(
        multipleWarehouse.listWarehouse,
        "",
        "",
        "",
        "",
        "",
        ""
      );

      const response = await apiService.callAPI();
      const data: WarehouseItem[] = [];

      if (response.records.length) {
        response.records.forEach((val: any) => {
          data.push({
            value: val._id,
            label: val.name,
            title: val.name,
            _id: val._id,
            defaultWarehouse: val.defaultWarehouse,
            district: val.district,
          });
        });
      }

      setWarehouseList(data);
      const nextSelected = resolveStoredWarehouses(data);
      setSelectedWarehousesState(nextSelected);
      setKey(warehouseStrings.warehouseRef, nextSelected);
    } catch (error) {
      console.error("Error fetching warehouse list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWarehouseList();
  }, []);

  return (
    <WarehouseContext.Provider
      value={{
        selectedWarehouse,
        setSelectedWarehouse,
        selectedWarehouses,
        setSelectedWarehouses,
        selectedWarehouseIds,
        isAllWarehousesSelected,
        warehouseList,
        warehouseOptions,
        loading,
        fetchWarehouseList,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};
